import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { uploadSessionSummaryUseCase } from 'src/services/therapy/useCases';
import { useAppDispatch, useAppSelector } from 'src/hooks';

interface UploadSummaryDialogProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId?: any;
}

const UploadSummaryDialog: React.FC<UploadSummaryDialogProps> = ({
  isOpen,
  onClose,
  sessionId,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['therapyAppointment']);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [comment, setComment] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [showCommentArea, setShowCommentArea] = useState(false);

  const { summaryUpload } = useAppSelector((state) => state.therapy);

  useEffect(() => {
    if (summaryUpload) {
      setSelectedFile(null);
      setComment('');
      setShowCommentArea(false);
    }
  }, [summaryUpload]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 3 * 1024 * 1024) {
        // 3MB limit
        alert(t('fileSizeExceeded', { defaultValue: 'File size exceeds 3MB limit' }));
        return;
      }
      if (file.type !== 'application/pdf') {
        alert(t('onlyPdfAllowed', { defaultValue: 'Only PDF files are allowed' }));
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.size > 3 * 1024 * 1024) {
        // 3MB limit
        alert(t('fileSizeExceeded', { defaultValue: 'File size exceeds 3MB limit' }));
        return;
      }
      if (file.type !== 'application/pdf') {
        alert(t('onlyPdfAllowed', { defaultValue: 'Only PDF files are allowed' }));
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = () => {
    if (selectedFile || comment) {
      onClose();
      dispatch(
        uploadSessionSummaryUseCase({
          appointmentId: sessionId,
          file: selectedFile,
          comment: comment,
        }),
      );
    }
  };

  const handleClickUploadArea = () => {
    fileInputRef.current?.click();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClickUploadArea();
    }
  };

  const handleAddComment = () => {
    setShowCommentArea(true);
  };

  const handleDoneComment = () => {
    setShowCommentArea(false);
  };

  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
      {!showCommentArea ? (
        <div
          className='bg-background rounded-2xl max-w-1/2 w-3/4 p-6 shadow-lg'
          style={{
            position: 'absolute',
            bottom: '80px',
            right: '100px',
            height: '62vh',
            width: '72%',
            alignContent: 'center',
          }}
        >
          <div style={{ position: 'relative', top: '5%' }}>
            <h2 className='text-center text-xl text-[#591D94] font-medium mb-2'>
              {t('uploadAfterSessionSummary', { defaultValue: 'Upload After Session Summary' })}
            </h2>

            <p className='text-center text-sm text-gray-600 mb-4'>
              {t('pleaseUploadPdf', {
                defaultValue: 'Please upload a PDF file (maximum size 3 MB)',
              })}
            </p>

            <div
              role='button'
              tabIndex={0}
              aria-label={t('uploadPdf', { defaultValue: 'Upload PDF file' })}
              className={`border-2 border-dashed rounded-lg p-8 mb-4 flex flex-col items-center justify-center cursor-pointer ${
                isDragging ? 'border-[#591D94] bg-purple-50' : 'border-gray-300'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={handleClickUploadArea}
              onKeyDown={handleKeyDown}
            >
              <input
                type='file'
                ref={fileInputRef}
                className='hidden'
                accept='.pdf'
                onChange={handleFileChange}
                disabled={comment !== ''}
              />

              <svg
                className='w-12 h-12 text-[#591D94] mb-2'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  d='M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12'
                />
              </svg>

              {selectedFile ? (
                <span className='text-sm text-gray-700'>{selectedFile.name}</span>
              ) : (
                <button className='bg-[#591D94] hover:bg-[#591D94] text-white py-1 px-4 rounded-md text-sm'>
                  {t('upload', { defaultValue: 'UPLOAD' })}
                </button>
              )}
            </div>

            <div className='mb-4 flex justify-center'>
              <p className='text-center text-sm text-gray-600 mr-1'>
                {t('orAddComment', { defaultValue: 'or' })}
              </p>
              <button
                className='text-[#591D94] text-sm'
                onClick={handleAddComment}
                disabled={selectedFile !== null}
              >
                {t('addComment', { defaultValue: 'Add a Comment' })}
              </button>
            </div>
            <p className='text-xs text-gray-500 mt-1 text-center'>
              {t('commentVisibilityNote', {
                defaultValue:
                  'You Can Add a Comment to Let us Know if the User Did Not Show Up for Their Session',
              })}
            </p>

            <div className='flex justify-between'>
              <button
                className='bg-gray-200 hover:bg-gray-300 text-gray-700 py-2 px-4 rounded-md'
                onClick={() => {
                  onClose();
                  setSelectedFile(null);
                  setComment('');
                }}
              >
                {t('cancel', { defaultValue: 'Cancel' })}
              </button>
              <button
                className={`bg-[#591D94] hover:bg-[#591D94] text-white py-2 px-4 rounded-md ${
                  !selectedFile && !comment ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                onClick={handleUpload}
                disabled={!selectedFile && !comment}
              >
                {t('submit', { defaultValue: 'Submit' })}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div
          className='bg-background rounded-2xl max-w-1/2 w-3
        /4 p-6 shadow-lg'
          style={{
            position: 'absolute',
            bottom: '20px',
            right: '62px',
            height: '62vh',
            width: '72%',
          }}
        >
          <div className='m-8' style={{ position: 'relative', top: '15%' }}>
            <h3 className='text-center text-md font-medium text-[#591D94] mb-2'>
              {t('addComment', { defaultValue: 'Add a Comment' })}
            </h3>
            <textarea
              className='w-full p-2 border bg-gray-200 border-gray-300 rounded-md h-40'
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('addCommentPlaceholder', { defaultValue: 'Add your comment here...' })}
            />
            <div className='flex justify-center mt-2'>
              <button
                className='bg-[#591D94] hover:bg-[#591D94] text-white py-1 px-4 rounded-md text-sm'
                onClick={handleDoneComment}
              >
                {t('done', { defaultValue: 'Done' })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadSummaryDialog;
